import { getCalendarEntryByFolderId, isWritableCalendar } from "owa-calendar-cache";
import { isLinkedCalendarEntry, isLocalCacheForRemoteCalendarEntry } from "owa-calendar-properties";
import type { CalendarEntry, LinkedCalendarEntry, LocalCacheForRemoteCalendarEntry, LocalCalendarEntry, } from "owa-graph-schema";
import type { MailboxInfo } from "owa-client-types";
import { accountSourceDataTypeChecker, getCoprincipalAccountForMailboxInfo, getCoprincipalMailboxInfoForMailboxInfo, isGlobalSettingsMailbox, getGlobalSettingsAccountMailboxInfo, } from "owa-account-source-list-store";
import { isAccountFeatureEnabled, isGlobalFeatureEnabled } from "owa-feature-flags";
import { getOwaWorkload, type OwaWorkload } from "owa-workloads";
import { getMailboxInfoForShared } from "owa-mailbox-info/lib/getMailboxInfoForShared";
import { WorkLocationControlType } from "./types";

export interface WorkLocationControlInfo {
    requiredControlType: WorkLocationControlType;
    sharedOwnerEmailAddress: string | null | undefined;
    mailboxInfo: MailboxInfo;
    isSecondaryMailbox: boolean;
}

export default function getWorkLocationControlRequirements(selectedMailboxInfo: MailboxInfo, calendarFolderId?: string): WorkLocationControlInfo {
    const isPlacesEnabled = getOwaWorkload() === 16777216 ||
        isAccountFeatureEnabled("cal-surface-places-callout", getGlobalSettingsAccountMailboxInfo());
    
    let requiredControlType = isPlacesEnabled
        ? WorkLocationControlType.PlacesReadWrite
        : WorkLocationControlType.ReadWrite;
    let sharedOwnerEmailAddress: string | null | undefined = undefined;
    let folderMailboxInfo = undefined;
    let isSecondaryMailbox = false;
    
    // This flow handles both single calendar view and split view. If a folder ID is passed in,
    // we should try to process the mailboxInfo permissions associated with that folder ID.
    if (calendarFolderId) {
        const calendarEntry: CalendarEntry | undefined = getCalendarEntryByFolderId(calendarFolderId);
        
        if (calendarEntry) {
            folderMailboxInfo = getCoprincipalMailboxInfoForMailboxInfo(calendarEntry.calendarId.mailboxInfo);
            if (isLinkedCalendarEntry(calendarEntry) && !calendarEntry.IsGroupMailboxCalendar) {
                // This covers part of our shared calendar scenario.
                requiredControlType = WorkLocationControlType.ReadOnly;
                sharedOwnerEmailAddress = (calendarEntry as LinkedCalendarEntry).OwnerEmailAddress;
            }
            else if (isLocalCacheForRemoteCalendarEntry(calendarEntry)) {
                // This covers the rest of our shared calendar scenario.
                requiredControlType = WorkLocationControlType.ReadOnly;
                sharedOwnerEmailAddress = (calendarEntry as LocalCacheForRemoteCalendarEntry)
                    .OwnerEmailAddress;
                
                // Here we validate and process mailboxInfos for our delegate scenario.
                // We need to validate the delegate properties and ensure that the coprincipal
                // mailbox is an M365 user mailbox before proceeding. This includes checking
                // the effective rights of the shared calendar and ensuring that it is a GS entry.
                if (sharedOwnerEmailAddress &&
                    isWritableCalendar(calendarEntry) &&
                    (calendarEntry as LocalCacheForRemoteCalendarEntry).IsGeneralScheduleCalendar &&
                    isGlobalFeatureEnabled("cal-surface-places-delegate") &&
                    folderMailboxInfo &&
                    accountSourceDataTypeChecker.isM365UserMailbox(getCoprincipalAccountForMailboxInfo(folderMailboxInfo))) {
                    // Here, we either retrieve the existing shared mailbox info for the delegate
                    // entry, or we create a new one.
                    const sharedMailboxInfo = getMailboxInfoForShared(folderMailboxInfo, sharedOwnerEmailAddress, (calendarEntry as LocalCacheForRemoteCalendarEntry).OwnerName ??
                        calendarEntry.CalendarName);
                    if (isAccountFeatureEnabled("cal-surface-places-callout", sharedMailboxInfo)) {
                        requiredControlType = isPlacesEnabled
                            ? WorkLocationControlType.PlacesReadWrite
                            : WorkLocationControlType.ReadWrite;
                        folderMailboxInfo = sharedMailboxInfo;
                        isSecondaryMailbox = true;
                    }
                }
            }
            else if (!(calendarEntry as LocalCalendarEntry).IsDefaultCalendar) {
                // This covers our non-shared non-primary calendar scenarios, such as Birthdays.
                requiredControlType = WorkLocationControlType.None;
            }
            else if (!isGlobalSettingsMailbox(calendarEntry.calendarId.mailboxInfo)) {
                // This covers our multi-account secondary account scenario.
                if (!isGlobalFeatureEnabled("cal-surface-places-multi-account") &&
                    isAccountFeatureEnabled("cal-surface-places-callout", folderMailboxInfo ?? getGlobalSettingsAccountMailboxInfo())) {
                    requiredControlType = WorkLocationControlType.ReadOnly;
                    sharedOwnerEmailAddress =
                        calendarEntry.calendarId.mailboxInfo.mailboxSmtpAddress;
                }
                isSecondaryMailbox = true;
            }
        }
    }
    else {
        // For our merged view scenario we need to explicitly check if the selected mailbox is a
        // secondary mailbox.
        isSecondaryMailbox = !isGlobalSettingsMailbox(selectedMailboxInfo);
    }
    
    return {
        requiredControlType,
        sharedOwnerEmailAddress,
        mailboxInfo: folderMailboxInfo ?? selectedMailboxInfo,
        isSecondaryMailbox,
    };
}
